import { useMemo } from "react";

import {
  endOfMonth,
  format,
  startOfMonth,
  subMonths,
  subWeeks,
} from "date-fns";
import { es } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Calendar } from "~/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { cn } from "~/lib/utils";

interface DateSelectorProps {
  selectedDate: string | null;
  onDateChange: (date: string) => void;
  availableDates: string[];
  loading?: boolean;
}

export const DateSelector = ({
  selectedDate,
  onDateChange,
  availableDates,
  loading = false,
}: DateSelectorProps) => {
  const availableDateSet = useMemo(
    () => new Set(availableDates),
    [availableDates]
  );

  const findNearestAvailableDate = (targetDate: Date): string | null => {
    const targetStr = format(targetDate, "yyyy-MM-dd");
    if (availableDateSet.has(targetStr)) {
      return targetStr;
    }

    // Find the nearest available date before target
    const sortedDates = [...availableDates].sort().reverse();
    for (const date of sortedDates) {
      if (date <= targetStr) {
        return date;
      }
    }

    // If no earlier date, return the earliest available
    return sortedDates[sortedDates.length - 1] || null;
  };

  const handleQuickNavigation = (
    period: "today" | "lastWeek" | "lastMonth" | "endPrevMonth"
  ) => {
    const today = new Date();
    let targetDate: Date;

    switch (period) {
      case "today":
        targetDate = today;
        break;
      case "lastWeek":
        targetDate = subWeeks(today, 1);
        break;
      case "lastMonth":
        targetDate = subMonths(today, 1);
        break;
      case "endPrevMonth":
        targetDate = endOfMonth(subMonths(startOfMonth(today), 1));
        break;
    }

    const nearestDate = findNearestAvailableDate(targetDate);
    if (nearestDate) {
      onDateChange(nearestDate);
    }
  };

  const handleCalendarSelect = (date: Date | undefined) => {
    if (date) {
      const dateStr = format(date, "yyyy-MM-dd");
      if (availableDateSet.has(dateStr)) {
        onDateChange(dateStr);
      }
    }
  };

  const selectedDateObj = selectedDate
    ? new Date(selectedDate + "T00:00:00")
    : undefined;

  const disabledDays = (date: Date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return !availableDateSet.has(dateStr);
  };

  const formattedDate = selectedDate
    ? format(new Date(selectedDate + "T00:00:00"), "d 'de' MMMM, yyyy", {
        locale: es,
      })
    : "Seleccionar fecha";

  return (
    <div className="space-y-3">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-full justify-start text-left font-normal md:w-[280px]",
              !selectedDate && "text-muted-foreground"
            )}
            disabled={loading}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {formattedDate}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selectedDateObj}
            onSelect={handleCalendarSelect}
            disabled={disabledDays}
            initialFocus
            locale={es}
          />
        </PopoverContent>
      </Popover>

      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleQuickNavigation("today")}
          disabled={loading || availableDates.length === 0}
        >
          Hoy
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleQuickNavigation("lastWeek")}
          disabled={loading || availableDates.length === 0}
        >
          Hace 1 semana
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleQuickNavigation("lastMonth")}
          disabled={loading || availableDates.length === 0}
        >
          Hace 1 mes
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleQuickNavigation("endPrevMonth")}
          disabled={loading || availableDates.length === 0}
        >
          Fin mes anterior
        </Button>
      </div>
    </div>
  );
};
